/**
 * The Ordering App's own Menu screen (ORDR-165), per `DESIGN/Menu.dc.html`.
 *
 * WHY THE APP DRAWS IT (operator decision, 2026-10-05). The framework's
 * `MenuBrowser` (ui-components 1.3.0) has no tags, portions, per-portion
 * deltas, nested groups or note, so the customer Menu, Item, Checkout and
 * Status screens are this app's, composed from framework PRIMITIVES (`Button`,
 * `Badge`, tokens and the runtime brand theme). It is a deliberate, recorded
 * exception to the no-duplicate rule for the customer flow only.
 *
 * Sections are chips and only the chosen section's items are listed. Each item
 * shows its name, what the chips do not carry (`description`), its allergen
 * and dietary chips, and its price: "From" the smallest size for a sized item.
 * "View cart" carries the count and the SERVER total; the total is absent until
 * the quote has answered, never estimated here.
 *
 * Colours are tokens only. The brand reaches them through `BrandedSurface`
 * (ORDR-139); nothing here names a colour.
 */

import { useState, type ReactElement } from "react";
import { Badge, Button } from "@pirouz-ca/ui-components";
import { formatMinor } from "../customer/money.js";
import type { CatalogueItem, CatalogueSection } from "./catalogue.js";

export interface MenuCartSummary {
  /** Units across every line. */
  readonly count: number;
  /** The server quote's total, formatted; `null` until it has answered. */
  readonly total: string | null;
}

export interface MenuScreenProps {
  readonly sections: readonly CatalogueSection[];
  readonly cart: MenuCartSummary;
  readonly onOpenItem: (item: CatalogueItem) => void;
  readonly onViewCart: () => void;
}

function priceLabel(item: CatalogueItem): string {
  if (item.portions.length === 0) {
    return item.unitAmountMinor === null ? "" : formatMinor(item.unitAmountMinor, item.currency);
  }
  const lowest = Math.min(...item.portions.map((portion) => portion.amountMinor));
  return `From ${formatMinor(lowest, item.currency)}`;
}

function ItemRow({ item, onOpen }: { item: CatalogueItem; onOpen: (item: CatalogueItem) => void }): ReactElement {
  return (
    <li className="border-t border-[var(--color-neutral-100)]">
      <button
        type="button"
        disabled={!item.available}
        onClick={() => onOpen(item)}
        className="flex w-full flex-col gap-1 px-5 py-3.5 text-left text-[var(--color-neutral-900)] disabled:opacity-60"
      >
        <span className="text-[17px] font-bold">{item.name}</span>
        {item.description !== "" && <span className="text-sm text-[var(--color-neutral-600)]">{item.description}</span>}
        {item.chips.length > 0 && (
          <span className="flex flex-wrap gap-1.5">
            {item.chips.map((chip) => (
              <Badge key={chip} variant="neutral" size="sm">
                {chip}
              </Badge>
            ))}
          </span>
        )}
        <span className="text-[15px] font-semibold">{item.available ? priceLabel(item) : "Sold out"}</span>
      </button>
    </li>
  );
}

export function MenuScreen({ sections, cart, onOpenItem, onViewCart }: MenuScreenProps): ReactElement {
  const [chosenId, setChosenId] = useState<string | null>(null);
  const chosen = sections.find((section) => section.id === chosenId) ?? sections[0];

  if (chosen === undefined) {
    return <p className="p-5 text-sm text-[var(--color-neutral-600)]">Nothing on the menu yet</p>;
  }

  return (
    <div className="flex flex-col" data-testid="order-menu">
      <nav aria-label="Menu sections" className="flex gap-2 overflow-x-auto px-5 py-3">
        {sections.map((section) => (
          <Button
            key={section.id}
            type="button"
            variant={section.id === chosen.id ? "primary" : "secondary"}
            aria-pressed={section.id === chosen.id}
            onClick={() => setChosenId(section.id)}
            className="min-h-11 shrink-0 rounded-full px-4 whitespace-nowrap"
          >
            {section.label}
          </Button>
        ))}
      </nav>
      <ul aria-label={chosen.label} className="flex flex-col">
        {chosen.items.map((item) => (
          <ItemRow key={item.offerRef} item={item} onOpen={onOpenItem} />
        ))}
      </ul>
      {cart.count > 0 && (
        <div className="sticky bottom-0 border-t border-[var(--color-neutral-200)] bg-[var(--color-neutral-50)] px-5 pt-3 pb-5">
          <button
            type="button"
            onClick={onViewCart}
            className="flex min-h-[52px] w-full items-center justify-between rounded-xl bg-[var(--color-primary-700)] px-[18px] text-[17px] font-bold text-[var(--color-neutral-50)]"
          >
            <span>{`View cart · ${String(cart.count)} ${cart.count === 1 ? "item" : "items"}`}</span>
            {cart.total !== null && <span>{cart.total}</span>}
          </button>
        </div>
      )}
    </div>
  );
}
